import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Search } from 'lucide-react';
import {
  Spotlight, SpotlightInput, SpotlightResults, SpotlightSection, SpotlightItem, SpotlightEmpty,
} from '@ofself/shell';
import { Button, useToast } from '../components/common';
import { DEFAULT_SLUG, guideHref, toChapters } from '../constants/docsGuides';
import api from '../services/api';
import { copyText } from '../utils/clipboard';
import './DocsPage.css';

// THE DOCS ARE A PAGE OF THE PORTAL, not a site beside it.
//
// This page used to mount its own website: `position: fixed` under the old top
// bar, its own grid backdrop, its own banner and mark, its own guide sidebar,
// its own sticky toolbar with its own hamburger, its own search modal, and an
// override that forced the whole surface white so it ignored the skin. Reading
// the docs meant leaving the portal — the sidebar opened them in a new tab.
//
// Everything structural is the kit's now: the surface is the portal stage
// (PortalLayout), the guide index is <Contents> (the kit's reading rail), search
// is <Spotlight> (the same overlay the portal's own search raises), and tooltips
// are <Tip>. What is left app-side is the part that is genuinely this page's:
// fetching guides, the GitHub-compatible anchor algorithm the docs' links are
// authored against, and keeping a heading pinned while a lazily-loaded Mermaid
// diagram reflows above it.

// ── Helpers ───────────────────────────────────────────────────────────────────

// GitHub's heading-slug algorithm: lowercase, drop punctuation (NOT spaces/hyphens),
// then spaces -> hyphens with NO collapsing. This matches the anchors authored in the
// markdown (e.g. "Part 4 · Automations & webhooks" -> "part-4--automations--webhooks",
// "1.1 Log in" -> "11-log-in"), so in-page and cross-doc #links resolve.
// scripts/check_devguide_freshness.py mirrors this function and fails CI on a dead
// anchor — keep the two in step.
function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 -]/g, '')  // drop punctuation; keep letters, digits, space, hyphen
    .replace(/ /g, '-');          // spaces -> hyphens (no run-collapsing, like GitHub)
}

// Flatten react-markdown heading children (strings + inline elements like <code>)
// to plain text, so heading ids match the TOC's (which slugifies the raw text).
function nodeText(node) {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (node.props && node.props.children != null) return nodeText(node.props.children);
  return '';
}

function extractToc(markdown) {
  const toc = [];
  const lines = markdown.split('\n');
  let inCodeBlock = false;
  for (const line of lines) {
    if (line.trimStart().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;
    const m = line.match(/^(#{1,4})\s+(.+)$/);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2]
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[`*_]/g, '')
      .trim();
    toc.push({ level, text, id: slugify(text) });
  }
  return toc;
}

// Body text under each heading, so ⌘K can find a sentence and not only a title.
//
// WHY THIS EXISTS. Search matched titles, descriptions and heading text, which
// meant the guides were searchable exactly to the extent their headings happened
// to carry keywords. "revocation never reaches a browser" is a real rule in
// data-placement.md and was unfindable; so was `get_upload_sas`, named only in a
// code block. The fix belongs here rather than in the docs: renaming a dozen
// headings to read like search terms makes every guide worse to READ, and the
// markdown is already cached client-side, so the index costs one pass over text
// we have.
//
// CODE BLOCKS ARE KEPT, unlike in extractToc. A developer searching for an
// identifier is the case this most needs to answer, and that identifier usually
// appears in a snippet rather than in prose.
//
// Attributed to the NEAREST PRECEDING HEADING so a result still scrolls
// somewhere: a match is offered as that section, not as a floating line.
function extractProse(markdown) {
  const sections = [];
  let heading = null;          // {text, id}
  let buf = [];

  const flush = () => {
    if (!heading) return;      // preamble above the first heading; the title covers it
    const body = buf.join(' ')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')   // link text, not the URL
      // Markdown furniture and table pipes. NOT `_`: stripping it turns
      // `get_upload_sas` into three words and makes every snake_case identifier
      // unfindable, which is the one case keeping code blocks was for. The guides
      // use `*` for emphasis, never `_`, so nothing is lost by leaving it.
      .replace(/[`*>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (body) sections.push({ ...heading, body });
    buf = [];
  };

  // Fences are TRACKED but their contents are KEPT: a `#` inside a code block is a
  // shell or Python comment, not a heading. Without this, `# failure: {...}` in a
  // snippet became a section whose label was that comment and whose anchor pointed
  // nowhere, and it cut the real section in half.
  let inCodeBlock = false;
  for (const line of markdown.split('\n')) {
    if (line.trimStart().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;                                  // the fence marker itself is not prose
    }
    const m = inCodeBlock ? null : line.match(/^(#{1,4})\s+(.+)$/);
    if (m) {
      flush();
      const text = m[2].replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/[`*_]/g, '').trim();
      heading = { text, id: slugify(text) };
      continue;
    }
    buf.push(line);
  }
  flush();
  return sections;
}

// A readable reason the section matched: the words around the hit, not the whole
// paragraph. Trimmed to word boundaries so it does not end mid-identifier.
function snippet(body, q, width = 90) {
  const at = body.toLowerCase().indexOf(q);
  if (at < 0) return '';
  let start = Math.max(0, at - Math.floor((width - q.length) / 2));
  let end = Math.min(body.length, start + width);
  if (start > 0) {
    const sp = body.indexOf(' ', start);
    if (sp > -1 && sp < at) start = sp + 1;
  }
  if (end < body.length) {
    const sp = body.lastIndexOf(' ', end);
    if (sp > at + q.length) end = sp;
  }
  return `${start > 0 ? '…' : ''}${body.slice(start, end).trim()}${end < body.length ? '…' : ''}`;
}

// ── Code block component with per-block copy ──────────────────────────────────

function CodeBlock({ children, className }) {
  // '' | 'copied' | 'failed' — "Copied" only once the clipboard took it.
  const [copyState, setCopyState] = useState('');
  const lang = (className || '').replace('language-', '') || '';
  const code = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    setCopyState((await copyText(code)) ? 'copied' : 'failed');
    setTimeout(() => setCopyState(''), 1500);
  };

  return (
    <div className="docs-code-block">
      <div className="docs-code-block__header">
        {lang && <span className="docs-code-block__lang">{lang}</span>}
        <button className="docs-code-block__copy" onClick={handleCopy} type="button">
          {copyState === 'copied' ? '✓ Copied' : copyState === 'failed' ? 'Could not copy' : 'Copy'}
        </button>
      </div>
      <pre><code className={className}>{code}</code></pre>
    </div>
  );
}

// In-memory cache so each guide is only fetched once per session.
// Switching back to a previously-viewed guide is instant — no fetch, no loading.
const guideCache = {};
// Extracted TOC headings per guide — built as guides are fetched.
const guideTocCache = {};
// Body prose per guide, sliced by the heading above it — see extractProse.
const guideProseCache = {};

// Lazy-load mermaid only when a diagram is actually rendered (keeps it out of the
// main bundle). Initialized once.
let _mermaidPromise = null;
function getMermaid() {
  if (!_mermaidPromise) {
    _mermaidPromise = import('mermaid').then((m) => {
      m.default.initialize({ startOnLoad: false, theme: 'neutral', securityLevel: 'loose' });
      return m.default;
    });
  }
  return _mermaidPromise;
}

// Cache rendered SVGs by diagram source, so re-renders/remounts (e.g. on scroll)
// reuse the SVG instantly instead of clearing and re-rendering (which flickered).
const _mermaidSvgCache = new Map();

// Renders a ```mermaid code block as an SVG diagram; falls back to the raw source on error.
function MermaidDiagram({ chart }) {
  const [svg, setSvg] = useState(() => _mermaidSvgCache.get(chart) || '');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (_mermaidSvgCache.has(chart)) { setSvg(_mermaidSvgCache.get(chart)); setFailed(false); return undefined; }
    let alive = true;
    const id = 'mmd-' + Math.random().toString(36).slice(2);
    getMermaid()
      .then((mermaid) => mermaid.render(id, chart))
      .then(({ svg }) => { _mermaidSvgCache.set(chart, svg); if (alive) { setSvg(svg); setFailed(false); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [chart]);
  if (failed) return <pre className="docs-mermaid-fallback"><code>{chart}</code></pre>;
  if (!svg) return <div className="docs-mermaid docs-mermaid--loading" aria-busy="true" />;
  return <div className="docs-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DocsPage({ slug: urlSlug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Guide index
  const [guides, setGuides] = useState([]);

  // When no slug is in the URL, load the default guide.
  const activeSlug = urlSlug || DEFAULT_SLUG;

  // Search overlay
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Current guide content
  const [markdown, setMarkdown] = useState('');
  const [activeId, setActiveId] = useState('');
  const [copiedAll, setCopiedAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);
  // The reading column is its own scroller, so the guide rail and the "on this
  // page" rail stay put while the prose moves.
  const docsRootRef = useRef(null);
  // Heading id we're scrolling to; re-pinned while late content (Mermaid) reflows.
  const pendingScrollRef = useRef(null);
  const pendingTimerRef = useRef(null);

  // Load guide index on mount, then preload all guides in the background
  // so ⌘K search can find headings across every guide.
  useEffect(() => {
    fetch(`${api.getBaseUrl()}/docs/guides`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setGuides(list);
        setLoading(false);
        // Preload all guides into cache (background, non-blocking)
        list.forEach(g => {
          if (guideCache[g.slug]) return;
          fetch(`${api.getBaseUrl()}/docs/guides/${g.slug}`)
            .then(r => r.ok ? r.text() : '')
            .then(md => {
              if (md) {
                guideCache[g.slug] = md;
                guideTocCache[g.slug] = extractToc(md);
                guideProseCache[g.slug] = extractProse(md);
              }
            })
            .catch(() => {});
        });
      })
      .catch(() => {
        setLoading(false);
        toast.error('Could not load documentation index.');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load guide content when activeSlug changes.
  // Cached guides swap in synchronously — no loading state at all.
  // First-time fetches keep the previous content visible while loading.
  useEffect(() => {
    if (!activeSlug) return;

    // Cache hit — instant swap, no loading indicator
    if (guideCache[activeSlug]) {
      const md = guideCache[activeSlug];
      setMarkdown(md);
      setLoading(false);
      if (docsRootRef.current) docsRootRef.current.scrollTop = 0;
      return;
    }

    // Cache miss — fetch, but keep previous content visible
    setLoading(true);
    let cancelled = false;
    fetch(`${api.getBaseUrl()}/docs/guides/${activeSlug}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(md => {
        if (cancelled) return;
        guideCache[activeSlug] = md;
        guideTocCache[activeSlug] = extractToc(md);
        guideProseCache[activeSlug] = extractProse(md);
        setMarkdown(md);
        setLoading(false);
        if (docsRootRef.current) docsRootRef.current.scrollTop = 0;
      })
      .catch(err => {
        if (cancelled) return;
        setLoading(false);
        toast.error(`Could not load guide (${err.message}).`);
      });
    return () => { cancelled = true; };
  }, [activeSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scrollspy — as the page scrolls, highlight the section whose heading is at
  // (or just above) the top of the viewport. Driven directly off the scroll
  // container so the right-rail indicator reliably follows along.
  useEffect(() => {
    const root = docsRootRef.current;
    if (!markdown || !root) return undefined;
    const onScroll = () => {
      // Only h2 — that is what the TOC shows, so the active marker tracks it
      // (a deeper h3 subsection keeps its parent highlighted).
      const headings = contentRef.current?.querySelectorAll('h2');
      if (!headings || !headings.length) return;
      const rootTop = root.getBoundingClientRect().top;
      const offset = 130; // px below the top counted as "current"
      let current = headings[0].id;
      for (const h of headings) {
        if (h.getBoundingClientRect().top - rootTop <= offset) current = h.id;
        else break;
      }
      if (current) setActiveId(current);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    const t = setTimeout(onScroll, 200); // initial sync after render
    return () => { clearTimeout(t); root.removeEventListener('scroll', onScroll); };
  }, [markdown]);

  // Keep the active TOC entry in view in the right rail as you scroll (Notion-style).
  useEffect(() => {
    if (!activeId) return;
    const el = document.querySelector('.docs-toc__item[data-active]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  // ⌘K / Ctrl+K opens search. The Spotlight owns Esc, the scrim and focus.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setSearchQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const activeGuide = guides.find(g => g.slug === activeSlug);
  const guideTitle = activeGuide?.title || 'Documentation';
  const chapters = useMemo(() => toChapters(guides), [guides]);

  // Search results — search across ALL guides and their sections, grouped by
  // guide with its matching sections beneath it.
  // Headings first, then body prose. Heading hits are the stronger signal — the
  // author named the section that — so they keep the top of each guide's group,
  // and a section matched both ways appears once.
  const searchGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const groups = [];
    for (const g of guides) {
      const titleMatch = g.title.toLowerCase().includes(q)
        || (g.description || '').toLowerCase().includes(q);

      const byHeading = (guideTocCache[g.slug] || [])
        .filter(item => item.text.toLowerCase().includes(q));
      const seen = new Set(byHeading.map(s => s.id));

      const byBody = (guideProseCache[g.slug] || [])
        .filter(s => !seen.has(s.id) && s.body.toLowerCase().includes(q))
        .map(s => ({ ...s, text: `${s.text} · ${snippet(s.body, q)}` }));

      const sections = [...byHeading, ...byBody].slice(0, 8);
      if (titleMatch || sections.length > 0) groups.push({ guide: g, sections });
    }
    return groups;
  }, [searchQuery, guides]);

  const guideFilename = `${activeSlug}.md`;

  // The guide's own leading "# Title" is the page title now, so it is lifted
  // out of the rendered body rather than printed twice. Copy and download
  // still hand over the whole file.
  const body = useMemo(() => markdown.replace(/^\s*#\s+[^\n]*\n?/, ''), [markdown]);
  const toc = useMemo(() => extractToc(body).filter(item => item.level === 2), [body]);

  // Guides numbered straight through the shelf, so "07" names one guide.
  const shelf = useMemo(() => {
    let n = 0;
    return chapters.map(c => ({
      ...c,
      entries: c.entries.map(e => ({ ...e, n: String(++n).padStart(2, '0') })),
    }));
  }, [chapters]);

  const handleCopyForClaude = useCallback(async () => {
    if (!(await copyText(markdown))) {
      toast.error('Could not copy. Use Download instead.');
      return;
    }
    setCopiedAll(true);
    toast.success(`${guideTitle} copied — paste it into your Claude project context.`);
    setTimeout(() => setCopiedAll(false), 3000);
  }, [markdown, guideTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = useCallback(() => {
    const blob = new Blob([markdown], { type: 'text/markdown; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = guideFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [markdown, guideFilename]);

  // scrollTop that places a heading just below the sticky toolbar (+ gap).
  // Independent of current scroll position, so it only changes when content above
  // the heading reflows (e.g. a lazy-loaded Mermaid diagram finally rendering).
  const targetTopFor = useCallback((el) => {
    const root = docsRootRef.current;
    if (!el || !root) return 0;
    const OFFSET = 24; // breathing room above the heading
    return Math.max(0, el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - OFFSET);
  }, []);

  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    const root = docsRootRef.current;
    if (!el || !root) return;
    // IMPORTANT: jump instantly, not smoothly. Mermaid loads via a lazy import, so
    // diagrams above the target expand up to a second *after* the click and shove the
    // heading down. A smooth animation toward the (pre-expansion) pixel would land on
    // whatever is at that pixel *after* the shift — i.e. an earlier section. Instead we
    // pin the heading to the top on every frame for ~1s, so as content above grows the
    // heading stays put and we end up exactly on it.
    const jump = () => {
      const e = document.getElementById(id);
      if (e) root.scrollTo({ top: targetTopFor(e) });
    };
    jump();
    pendingScrollRef.current = id;
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = setTimeout(() => { pendingScrollRef.current = null; }, 2500);
    let frames = 0;
    const pin = () => {
      if (pendingScrollRef.current !== id) return; // cancelled (user scrolled / new click)
      jump();
      if (frames++ < 70) requestAnimationFrame(pin);
    };
    requestAnimationFrame(pin);
  }, [targetTopFor]);

  // Read current slug from a ref so the (memoized) link handler always sees the latest
  // without being rebuilt per guide.
  const activeSlugRef = useRef(activeSlug);
  activeSlugRef.current = activeSlug;

  // Open a guide (optionally at an anchor) from anywhere on the page.
  const openGuide = useCallback((slug, hash) => {
    if (slug === activeSlugRef.current) {
      if (hash) scrollToId(hash);
      else docsRootRef.current?.scrollTo({ top: 0 });
      return;
    }
    const base = guideHref(slug);
    navigate(hash ? `${base}#${hash}` : base); // the hash effect scrolls once it renders
  }, [navigate, scrollToId]);

  // Intercept internal markdown links so they scroll/navigate within the SPA instead of
  // doing a native (failing) anchor jump or a full-page reload. Two internal shapes reach
  // us: same-page "#anchor", and cross-doc "/documentation/<slug>[#anchor]" (rewritten by
  // the backend from "OTHER_DOC.md#anchor"). External http(s) links fall through untouched.
  const handleDocLink = useCallback((e, href) => {
    if (!href) return;
    if (href.startsWith('#')) {
      e.preventDefault();
      const id = decodeURIComponent(href.slice(1));
      scrollToId(id);
      window.history.replaceState(null, '', `#${id}`); // reflect in URL, no router churn
      return;
    }
    if (href.startsWith('/documentation')) {
      e.preventDefault();
      const [path, hash] = href.split('#');
      const slug = (path === '/documentation' || path === '/documentation/')
        ? DEFAULT_SLUG
        : path.replace(/^\/documentation\//, '').replace(/\/$/, '');
      openGuide(slug, hash ? decodeURIComponent(hash) : undefined);
    }
  }, [openGuide, scrollToId]);

  // Re-pin the pending heading whenever the rendered content changes height (a diagram
  // finished rendering), and cancel the pin the moment the user scrolls themselves.
  useEffect(() => {
    const content = contentRef.current;
    const root = docsRootRef.current;
    if (!content || !root) return undefined;
    const repin = () => {
      const id = pendingScrollRef.current;
      if (!id) return;
      const el = document.getElementById(id);
      if (el) root.scrollTo({ top: targetTopFor(el) }); // instant: keep the heading put
    };
    const cancelPin = () => {
      pendingScrollRef.current = null;
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    };
    const ro = new ResizeObserver(repin);
    ro.observe(content);
    root.addEventListener('wheel', cancelPin, { passive: true });
    root.addEventListener('touchmove', cancelPin, { passive: true });
    root.addEventListener('keydown', cancelPin);
    return () => {
      ro.disconnect();
      root.removeEventListener('wheel', cancelPin);
      root.removeEventListener('touchmove', cancelPin);
      root.removeEventListener('keydown', cancelPin);
    };
  }, [markdown, targetTopFor]);

  // After a guide renders (or the URL hash changes), scroll to the #anchor. This handles
  // cross-doc links (navigate sets the hash, the new guide loads, then we scroll) and
  // deep links pasted/opened with a #fragment. scrollToId pins through late diagram loads.
  useEffect(() => {
    if (!markdown) return undefined;
    const hash = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    if (!hash) return undefined;
    const raf = requestAnimationFrame(() => scrollToId(hash));
    return () => cancelAnimationFrame(raf);
  }, [markdown, location.hash, scrollToId]);

  // react-markdown custom renderers (memoized so scroll re-renders don't rebuild the
  // tree and remount diagrams). Heading ids use nodeText(children) — plain text that
  // matches the TOC's slugify(rawText), so anchors resolve to the exact heading even
  // when the heading contains inline `code`.
  const components = useMemo(() => ({
    h1: ({ children }) => <h1 id={slugify(nodeText(children))}>{children}</h1>,
    h2: ({ children }) => <h2 id={slugify(nodeText(children))}>{children}</h2>,
    h3: ({ children }) => <h3 id={slugify(nodeText(children))}>{children}</h3>,
    h4: ({ children }) => <h4 id={slugify(nodeText(children))}>{children}</h4>,
    // react-markdown v10 dropped the `inline` flag: fenced blocks render as
    // <pre><code>, inline code as a bare <code>. So render blocks via `pre` (pull the
    // inner code's text + language) and treat every `code` the renderer sees as inline.
    pre({ children }) {
      const codeEl = Array.isArray(children) ? children.find((c) => c && c.props) : children;
      const className = (codeEl && codeEl.props && codeEl.props.className) || '';
      const codeText = nodeText(codeEl && codeEl.props && codeEl.props.children);
      if (/\blanguage-mermaid\b/.test(className)) return <MermaidDiagram chart={codeText.trim()} />;
      return <CodeBlock className={className}>{codeText}</CodeBlock>;
    },
    code: ({ className, children, ...props }) => (
      <code className="docs-inline-code" {...props}>{children}</code>
    ),
    table: ({ children }) => (
      <div className="docs-table-wrapper"><table className="docs-table">{children}</table></div>
    ),
    a: ({ href, children }) => {
      const external = !!href && /^https?:\/\//.test(href);
      return (
        <a
          href={href}
          target={external ? '_blank' : undefined}
          rel="noopener noreferrer"
          onClick={external ? undefined : (e) => handleDocLink(e, href)}
        >
          {children}
        </a>
      );
    },
    blockquote: ({ children }) => <blockquote className="docs-blockquote">{children}</blockquote>,
    hr: () => <hr className="docs-divider" />,
  }), [handleDocLink]);

  // Render the markdown once per document (not per scroll). Without this, the
  // scrollspy's setActiveId on every scroll re-rendered ReactMarkdown and made the
  // Mermaid diagrams flash.
  const renderedDoc = useMemo(() => (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {body}
    </ReactMarkdown>
  ), [body, components]);

  const openSearch = () => { setSearchOpen(true); setSearchQuery(''); };

  return (
    <div className="docs" ref={docsRootRef}>
      <div className="docs__frame">
        {/* ── The guide shelf, by chapter, numbered straight through ── */}
        <nav className="docs-nav" aria-label="Guides">
          {shelf.map(c => (
            <div key={c.label} className="docs-nav__chapter">
              <div className="docs-nav__group">{c.num} {c.label}</div>
              {c.entries.map(e => {
                const on = e.href === guideHref(activeSlug);
                return (
                  <a
                    key={e.href}
                    href={e.href}
                    className="docs-nav__item"
                    data-active={on || undefined}
                    aria-current={on ? 'page' : undefined}
                    onClick={(ev) => { ev.preventDefault(); navigate(e.href); }}
                  >
                    <span className="docs-nav__n">{e.n}</span>
                    <span>{e.title}</span>
                  </a>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── The article ── */}
        <article className="docs-doc">
          <div className="docs-doc__bar">
            <button className="docs-search-trigger" onClick={openSearch} type="button">
              <Search size={14} aria-hidden="true" />
              <span className="docs-search-trigger__text">Search the documentation</span>
              <kbd className="docs-search-trigger__kbd">⌘K</kbd>
            </button>
            <Button variant="secondary" size="sm" onClick={handleCopyForClaude} disabled={!markdown}>
              {copiedAll ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
              {copiedAll ? 'Copied' : 'Copy the whole guide'}
            </Button>
            <button className="docs-doc__quiet" onClick={handleDownload} type="button" disabled={!markdown}>
              Download {guideFilename}
            </button>
          </div>

          {/* Thin progress bar while fetching */}
          {loading && <div className="docs-loading-bar" />}

          <header className="docs-doc__head">
            <h1>{guideTitle}</h1>
            {activeGuide?.description && <p className="docs-doc__lede">{activeGuide.description}</p>}
          </header>

          {markdown && (
            <div className={`docs-content${loading ? ' docs-content--loading' : ''}`} ref={contentRef}>
              {renderedDoc}
            </div>
          )}
        </article>

        {/* ── On this page ── */}
        <aside className="docs-toc-rail" aria-label="On this page">
          {toc.length > 0 && (
            <>
              <div className="docs-toc-rail__label">On this page</div>
              <nav className="docs-toc">
                {toc.map((item, i) => (
                  <button
                    key={`${item.id}-${i}`}
                    type="button"
                    className="docs-toc__item"
                    data-active={activeId === item.id || undefined}
                    onClick={() => scrollToId(item.id)}
                  >
                    {item.text}
                  </button>
                ))}
              </nav>
            </>
          )}
        </aside>
      </div>

      {/* ── Search: the same overlay the portal's own search raises ── */}
      <Spotlight
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(''); }}
        label="Search the documentation"
      >
        <SpotlightInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search the documentation"
        />
        <SpotlightResults>
          {searchGroups.map(({ guide, sections }) => (
            <SpotlightSection key={guide.slug} title={guide.title}>
              <SpotlightItem
                as="button"
                onClick={() => { setSearchOpen(false); openGuide(guide.slug); }}
              >
                {guide.description || guide.title}
              </SpotlightItem>
              {sections.map((s) => (
                <SpotlightItem
                  key={s.id}
                  as="button"
                  onClick={() => { setSearchOpen(false); openGuide(guide.slug, s.id); }}
                >
                  {s.text}
                </SpotlightItem>
              ))}
            </SpotlightSection>
          ))}
          {searchQuery.trim() && !searchGroups.length && (
            <SpotlightEmpty>Nothing matches “{searchQuery}”.</SpotlightEmpty>
          )}
        </SpotlightResults>
      </Spotlight>
    </div>
  );
}
